/**
 * "Your Action Plan" on the Dashboard: one journey per goal, its steps, and the documents each step
 * opens (owner, 3 Oct 2026 rework).
 *
 * Every chip now opens something real — a pre-filled answer sheet for an official form, a checklist,
 * or the page in the app where the work is done (features/forms/actionPlanDocuments.ts). A step that
 * needs something we cannot provide yet shows it greyed as "coming soon" rather than promising it.
 *
 * The means assessment opens a chooser rather than a form, because which form is right turns on a
 * fact the tool can only estimate — whether the client actually receives the Age Pension today — and,
 * for a couple, on who is going into care. It replaces the old position pop-up, whose Continue button
 * closed it and produced nothing.
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, ArrowRight, X, Info, CheckCircle, ChevronDown, Clock } from 'lucide-react';
import { Card, IconTile } from '../../components/ui/index.tsx';
import { SectionHeader } from './DashboardHelpers.tsx';
import { WORKFLOW_CONFIGS } from './dashboardConfig.tsx';
import type { WorkflowConfig, WorkflowStepConfig, SavedStrategy } from './dashboardConfig.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { docChip, partnerDownsizerChip, openDocChipSheet, meansFormFor, type DocChip, type DocContext, type DocId, type MeansForm } from '../../features/forms/actionPlanDocuments.ts';
import { openOfficialForm } from '../../features/forms/officialForms.ts';
import { openPensionClaimForm, openModRForm, modRProperties, modRLabel, type PensionClaimFormCode, type ModRProperty } from '../../features/forms/pensionClaimForms.ts';
import { isCouple, personOf, ownsHome, likelyCareEntrant, type Who } from '../../features/forms/profileFacts.ts';
import { plansOfTypes, type SavedPlan } from '../../features/forms/planPosition.ts';
import { PositionPicker, ReviewEveryQuestion, useResolvedPosition, type PositionChoice } from './FormPosition.tsx';
import { withPensionSteps } from './pensionSteps.ts';
import { MYGOV_URL } from '../../features/forms/actionPlanDocuments.ts';

/* ─── The means-assessment chooser ─────────────────────────────────────────────────────────────── */

interface MeansChooserProps {
    factFind: DraftFactFind;
    setting: 'residential' | 'home';
    receivesPension: boolean;
    /** The saved plans this assessment can be filled from "after": aged care, or Support at Home. */
    plans: SavedPlan[];
    onClose: () => void;
}

function MeansChooser({ factFind, setting, receivesPension, plans, onClose }: MeansChooserProps) {
    const couple = isCouple(factFind);
    const [who, setWho] = useState<Who>(likelyCareEntrant(factFind));
    const recommended = meansFormFor(factFind, setting, receivesPension);
    const owns = ownsHome(factFind);
    const name = (w: Who) => personOf(factFind, w).first || (w === 'client' ? 'Client' : 'Partner');
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    // Today's position by default: the form most people lodge now (owner, 3 Oct 2026).
    const [choice, setChoice] = useState<PositionChoice>({ mode: 'today' });
    const position = useResolvedPosition(factFind, plans, choice);
    /*
     * Every form comes back as the OFFICIAL Services Australia PDF, filled in from the profile
     * (owner, 3 Oct 2026: "all the official forms"), today's or as it will stand after the plan.
     */
    const open = (form: Exclude<MeansForm, 'none'>) => {
        setBusy(true);
        setFailed(null);
        openOfficialForm(form, position.factFind, who, { afterPlan: position.afterPlan, facts: position.facts })
            .catch(() => setFailed('The form could not be prepared. Check your connection and try again.'))
            .finally(() => setBusy(false));
    };

    const why = setting === 'home'
        ? (recommended === 'none'
            ? 'Because you receive the Age Pension, Services Australia already holds what it needs to work out your Support at Home contribution. There is no form — just keep your income and assets up to date in myGov.'
            : 'Because you do not receive the Age Pension, Services Australia needs your income and assets to work out your Support at Home contribution. That is the SA456.')
        : (recommended === 'SA485'
            ? 'Because you receive the Age Pension, Services Australia already holds your income and assets. You only need to tell them about your home, on the shorter SA485.'
            : recommended === 'SA457'
                ? 'Because you do not receive the Age Pension, Services Australia needs your income and assets to work out your aged care fees. That is the SA457.'
                : `Because you receive the Age Pension${owns ? '' : ' and do not own a home'}, Services Australia can assess you automatically when you enter care — generally no form is needed. Keep your details up to date in myGov.`);

    const formButton = (form: Exclude<MeansForm, 'none'>, primary: boolean, label: string) => (
        <button
            type="button"
            onClick={() => open(form)}
            disabled={busy || position.status !== 'ready'}
            className={`flex w-full disabled:opacity-60 items-center justify-between rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors ${primary ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-800'}`}
        >
            <span>{label}</span>
            <ArrowRight size={16} />
        </button>
    );

    // Opened over the whole page: inside the card it was clipped and covered by the next tile.
    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="means-chooser-title">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-5">
                    <div>
                        <h3 id="means-chooser-title" className="text-lg font-bold text-gray-900">{setting === 'home' ? 'Your Support at Home contribution assessment' : 'Your aged care means assessment'}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">Pre-filled from your profile, question by question.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-gray-400 hover:text-gray-600"><X size={20} /></button>
                </div>
                <div className="space-y-4 p-5">
                    {couple && (
                        <div>
                            <p className="text-sm font-semibold text-gray-800 mb-2">Who is the assessment for?</p>
                            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Who is the assessment for">
                                {(['client', 'partner'] as Who[]).map((w) => (
                                    <button key={w} type="button" role="radio" aria-checked={who === w} onClick={() => setWho(w)} className={`rounded-lg border-2 px-3 py-2 text-sm font-semibold ${who === w ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'}`}>
                                        {name(w)}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">If you both need an assessment, each completes their own form.</p>
                        </div>
                    )}
                    <div className="flex gap-2 rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white p-3 text-sm text-slate-700">
                        <Info size={16} className="mt-0.5 shrink-0 text-blue-600" />
                        <p>{why}</p>
                    </div>
                    {!receivesPension && plans.some((p) => (Number(p.outputs?.agePension) || 0) > 0) && (
                        <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                            Your plan makes you eligible for the Age Pension. Claim it first, once the changes are made: when you are paid it, {setting === 'home' ? 'no SA456 is needed' : 'you need only the SA485 (or no form if there is no home)'}, not the {setting === 'home' ? 'SA456' : 'SA457'}.
                        </p>
                    )}
                    <PositionPicker plans={plans} choice={choice} onChange={setChoice} resolved={position} />
                    <ReviewEveryQuestion blanks="reference numbers, addresses and anything your profile doesn’t ask about" />
                    <div className="space-y-2">
                        {setting === 'home' ? (
                            recommended === 'SA456'
                                ? formButton('SA456', true, 'Open the SA456, pre-filled')
                                : formButton('SA456', false, 'Not receiving the Age Pension? Open the SA456')
                        ) : (
                            <>
                                {recommended === 'SA485' && formButton('SA485', true, 'Open the SA485, pre-filled')}
                                {recommended === 'SA457' && formButton('SA457', true, 'Open the SA457, pre-filled')}
                                {recommended !== 'SA457' && formButton('SA457', false, 'Not receiving the Age Pension? Open the SA457')}
                                {recommended !== 'SA485' && owns && formButton('SA485', false, 'Receiving the Age Pension and own a home? Open the SA485')}
                            </>
                        )}
                    </div>
                    {failed && <p className="text-sm text-red-700">{failed}</p>}
                    <p className="text-xs text-slate-500">Each form opens as the official Services Australia form. Where you have more accounts or investments than the form has room for, list the rest on a separate sheet, as the form asks.</p>
                    <p className="text-xs text-slate-500">Whether you receive the Age Pension is about your Centrelink account today, not the estimate in this tool — pick the form that matches your situation.</p>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* ─── Claiming the Age Pension ───────────────────────────────────────────────────────────────── */

/** The day someone can first lodge a claim: 13 weeks before Age Pension age (67). */
function claimOpensOn(dob: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob);
    if (!m) return null;
    const pensionAge = new Date(Number(m[1]) + 67, Number(m[2]) - 1, Number(m[3]));
    return new Date(pensionAge.getTime() - 13 * 7 * 24 * 3600 * 1000);
}

interface PensionClaimChooserProps {
    factFind: DraftFactFind;
    plans: SavedPlan[];
    onClose: () => void;
}

/**
 * The claim itself: online through myGov, or on paper as the SA002 and SA369 together, pre-filled.
 * Filled from the position after the plan by default, because the plan is what makes the claim worth
 * making; the reminder beside it says not to lodge until the changes are made.
 */
function PensionClaimChooser({ factFind, plans, onClose }: PensionClaimChooserProps) {
    const couple = isCouple(factFind);
    const name = (w: Who) => personOf(factFind, w).first || (w === 'client' ? 'Client' : 'Partner');
    const [claimants, setClaimants] = useState<Who[]>(couple ? ['client', 'partner'] : ['client']);
    const [choice, setChoice] = useState<PositionChoice>(plans[0]?.id ? { mode: 'plan', planId: String(plans[0].id) } : { mode: 'today' });
    const position = useResolvedPosition(factFind, plans, choice);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const open = (code: PensionClaimFormCode) => {
        setBusy(true);
        setFailed(null);
        openPensionClaimForm(code, position.factFind, claimants, { positionLabel: position.afterPlan ? 'after the plan' : 'today', afterPlan: position.afterPlan, facts: position.facts })
            .catch(() => setFailed('The form could not be prepared. Check your connection and try again.'))
            .finally(() => setBusy(false));
    };
    /* A Mod R for each property the SA369 asks about, from the same position. */
    const properties = useMemo(() => modRProperties(position.factFind, position.facts), [position.factFind, position.facts]);
    const openModR = (property: ModRProperty) => {
        setBusy(true);
        setFailed(null);
        openModRForm(position.factFind, property, claimants, { positionLabel: position.afterPlan ? 'after the plan' : 'today', afterPlan: position.afterPlan, facts: position.facts })
            .catch(() => setFailed('The form could not be prepared. Check your connection and try again.'))
            .finally(() => setBusy(false));
    };
    const today = new Date();
    const notYet = claimants
        .map((w) => ({ w, opens: claimOpensOn(personOf(factFind, w).dob) }))
        .filter((x) => x.opens && x.opens > today);
    const options: Array<{ key: string; label: string; who: Who[] }> = couple
        ? [{ key: 'both', label: 'Both of us', who: ['client', 'partner'] }, { key: 'client', label: name('client'), who: ['client'] }, { key: 'partner', label: name('partner'), who: ['partner'] }]
        : [];
    const btn = (code: PensionClaimFormCode, label: string) => (
        <button type="button" onClick={() => open(code)} disabled={busy || position.status !== 'ready'} className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-800 disabled:opacity-60">
            <span>{label}</span>
            <ArrowRight size={16} />
        </button>
    );
    // Opened over the whole page: inside the card it was clipped and covered by the next tile.
    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="pension-claim-title">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-5">
                    <div>
                        <h3 id="pension-claim-title" className="text-lg font-bold text-gray-900">Claim the Age Pension</h3>
                        <p className="text-sm text-slate-500 mt-0.5">It is not paid automatically. You have to claim it.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-gray-400 hover:text-gray-600"><X size={20} /></button>
                </div>
                <div className="space-y-4 p-5">
                    {couple && (
                        <div>
                            <p className="text-sm font-semibold text-gray-800 mb-2">Who is claiming?</p>
                            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Who is claiming">
                                {options.map((o) => {
                                    const on = o.who.join() === claimants.join();
                                    return (
                                        <button key={o.key} type="button" role="radio" aria-checked={on} onClick={() => setClaimants(o.who)} className={`rounded-lg border-2 px-3 py-2 text-sm font-semibold ${on ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'}`}>
                                            {o.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">A couple both claiming can use the one SA002.</p>
                        </div>
                    )}
                    {notYet.map((x) => (
                        <p key={x.w} className="text-xs text-amber-800">{name(x.w)} can lodge a claim from {x.opens!.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}, 13 weeks before Age Pension age.</p>
                    ))}
                    <PositionPicker plans={plans} choice={choice} onChange={setChoice} resolved={position} />
                    <ReviewEveryQuestion blanks="reference numbers, your address, the bank account for payments, your tax file number and your residence history" />
                    <div className="space-y-2">
                        <a href={MYGOV_URL} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-between rounded-lg border border-blue-600 bg-blue-600 px-4 py-3 text-left text-sm font-semibold text-white transition-colors hover:bg-blue-700">
                            <span>Claim online through myGov (quickest)</span>
                            <ArrowRight size={16} />
                        </a>
                        {btn('SA002', 'Or on paper: the SA002 claim, pre-filled')}
                        {btn('SA369', 'With it: the SA369 income and assets, pre-filled')}
                        {properties.map((pr) => (
                            <button key={pr.kind === 'home' ? 'home' : `ip-${pr.index}`} type="button" onClick={() => openModR(pr)} disabled={busy || position.status !== 'ready'} className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-800 disabled:opacity-60">
                                <span>With it: real estate details (Mod R) for {modRLabel(pr)}, pre-filled</span>
                                <ArrowRight size={16} />
                            </button>
                        ))}
                    </div>
                    {failed && <p className="text-sm text-red-700">{failed}</p>}
                    <p className="text-xs text-slate-500">On paper, lodge the SA002 and the SA369 together{properties.length ? ', with a Mod R for each property listed' : ''}. Each opens as the official Services Australia form.</p>
                    <p className="text-xs text-slate-500">The pension is generally paid from the day you claim, not before, so claim promptly once the changes are made.</p>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* ─── The journey and its steps ──────────────────────────────────────────────────────────────── */

/**
 * The means assessment step, said for this client. Residential: SA485 (pension + home), SA457 (no
 * pension), or usually nothing (pension, no home). Support at Home: SA456 (no pension) or nothing.
 */
function adaptFinancialAssessmentStep(step: WorkflowStepConfig, workflowKey: string, receivesPension: boolean, factFind: DraftFactFind): WorkflowStepConfig {
    if (workflowKey === 'aged_care' && step.id === 'sa457_form') {
        const form = meansFormFor(factFind, 'residential', receivesPension);
        if (form === 'SA485') return { ...step, name: 'Confirm your home details (SA485)', description: 'You receive the Age Pension, so Services Australia already holds your income and assets. You only confirm your home on the shorter SA485 — pre-filled from your profile. Homes ask for the letter it produces, so do it before applying.', estimatedTime: '~5 min (pre-filled)' };
        if (form === 'SA457') return { ...step, name: 'Means assessment (SA457)', description: 'As you do not receive the Age Pension, the SA457 tells Services Australia your income and assets so it can work out your fees — pre-filled from your profile. Homes ask for the letter it produces, so do it before applying.', estimatedTime: '~15 min (pre-filled)' };
        return { ...step, name: 'Means assessment (usually automatic)', description: 'You receive the Age Pension and do not own a home, so Services Australia can assess your fees automatically when you enter care. Keep your details in myGov up to date.', estimatedTime: 'Usually none' };
    }
    if (workflowKey === 'home_care' && step.id === 'sah_means_form') {
        const form = meansFormFor(factFind, 'home', receivesPension);
        if (form === 'SA456') return { ...step, name: 'Contribution assessment (SA456)', description: 'As you do not receive the Age Pension, the SA456 tells Services Australia your income and assets so it can work out your contribution — pre-filled from your profile. Without it your provider can charge the maximum.', estimatedTime: '~15 min (pre-filled)' };
        return { ...step, name: 'Contribution assessment (already on file)', description: 'You receive the Age Pension, so Services Australia already has what it needs — no form. Keep your income and assets up to date in myGov.', estimatedTime: 'Usually none' };
    }
    return step;
}

/** The chips a step shows for this client — the downsizer form only where the plan makes a contribution, and the partner's own. */
function chipsForStep(step: WorkflowStepConfig, ctx: DocContext): DocChip[] {
    const chips = step.forms.map((f) => docChip(f as DocId, ctx)).filter((c): c is DocChip => !!c);
    if (step.forms.includes('downsizer_contribution')) {
        const partner = partnerDownsizerChip(ctx);
        if (partner) chips.push(partner);
    }
    return chips;
}

interface WorkflowStepProps {
    step: WorkflowStepConfig;
    index: number;
    isCompleted: boolean;
    isNext: boolean;
    chips: DocChip[];
    onChip: (chip: DocChip) => void;
}

function WorkflowStep({ step, index, isCompleted, isNext, chips, onChip }: WorkflowStepProps) {
    const statusIcon = isCompleted
        ? <CheckCircle size={20} className="text-green-500" />
        : <div className={`w-5 h-5 ${isNext ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600'} rounded-full flex items-center justify-center text-xs font-bold`}>{index}</div>;
    const edge = isCompleted ? 'border-l-green-500' : isNext ? 'border-l-blue-500' : 'border-l-slate-300';
    return (
        <div className={`p-4 rounded-lg bg-slate-50 border border-slate-200 border-l-4 ${edge} transition-colors`}>
            <div className="flex items-start gap-3">
                {statusIcon}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                        <h4 className={`font-semibold ${isCompleted ? 'text-green-700' : isNext ? 'text-blue-700' : 'text-gray-700'}`}>{step.name}</h4>
                        <span className="text-xs text-gray-500 shrink-0">{step.estimatedTime}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                    {(chips.length > 0 || (step.comingSoon?.length ?? 0) > 0) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {chips.map((chip) => (
                                <button key={`${chip.id}-${chip.label}`} type="button" onClick={() => onChip(chip)} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded text-xs font-medium text-blue-700 border border-blue-200 hover:border-blue-400 hover:text-blue-900 transition-colors">
                                    <FileText size={12} /> {chip.label}
                                </button>
                            ))}
                            {(step.comingSoon || []).map((label) => (
                                <span key={label} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded text-xs text-slate-400 border border-slate-200 cursor-not-allowed" title="Not available yet">
                                    <Clock size={12} /> {label} — coming soon
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

interface CollapsibleWorkflowProps {
    workflow: WorkflowConfig;
    workflowKey: string;
    completedIds: string[];
    isExpanded: boolean;
    onToggle: () => void;
    ctx: DocContext;
    onChip: (chip: DocChip) => void;
}

function CollapsibleWorkflow({ workflow, workflowKey, completedIds, isExpanded, onToggle, ctx, onChip }: CollapsibleWorkflowProps) {
    const steps = withPensionSteps(workflow.steps.map((s) => adaptFinancialAssessmentStep(s, workflowKey, ctx.receivesPension, ctx.factFind)), workflowKey, ctx);
    const total = steps.length;
    const done = completedIds.length;
    const nextStep = steps.find((s) => !completedIds.includes(s.id));
    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button type="button" className="w-full text-left bg-slate-50 p-4 hover:bg-slate-100 transition-colors" onClick={onToggle} aria-expanded={isExpanded}>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <IconTile icon={workflow.icon} tone="brand" variant="solid" size="md" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">{workflow.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                                <div className="w-24 bg-gray-200 rounded-full h-1.5">
                                    <div className="bg-gradient-to-r from-blue-500 to-teal-400 h-1.5 rounded-full transition-all duration-500" style={{ width: `${(done / total) * 100}%` }}></div>
                                </div>
                                <span className="text-xs text-gray-600">{done}/{total}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {nextStep && <span className="text-sm text-gray-600">Next: {nextStep.name}</span>}
                        <ChevronDown size={20} className={`text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                </div>
            </button>
            {isExpanded && (
                <div className="p-4 space-y-3 bg-white">
                    {steps.map((step, index) => (
                        <WorkflowStep
                            key={step.id}
                            step={step}
                            index={index + 1}
                            isCompleted={completedIds.includes(step.id)}
                            isNext={nextStep?.id === step.id}
                            chips={chipsForStep(step, ctx)}
                            onChip={onChip}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── The card ──────────────────────────────────────────────────────────────────────────────── */

export interface WorkflowApplicationsFormsCardProps {
    factFind: DraftFactFind;
    setActivePage: (page: string) => void;
    savedStrategies: SavedStrategy[];
    receivesPension?: boolean;
    /** Today's Age Pension a year: a plan's pension is compared with it to decide the claim or report step. */
    currentPension?: number;
}

type WorkflowKey = keyof typeof WORKFLOW_CONFIGS;

export default function WorkflowApplicationsFormsCard({ factFind, setActivePage, savedStrategies, receivesPension = false, currentPension = 0 }: WorkflowApplicationsFormsCardProps) {
    const { goals, careNeeds } = factFind;
    const couple = isCouple(factFind);
    const [expandedWorkflows, setExpandedWorkflows] = useState<Record<string, boolean>>({});
    const [meansSetting, setMeansSetting] = useState<'residential' | 'home' | null>(null);
    const [claimOpen, setClaimOpen] = useState(false);
    const ctx: DocContext = useMemo(() => ({ factFind, savedStrategies: savedStrategies as never, receivesPension, currentPension }), [factFind, savedStrategies, receivesPension, currentPension]);
    /* The plans each chooser can fill a form from "after", newest first. */
    const residentialPlans = useMemo(() => plansOfTypes(savedStrategies as SavedPlan[], ['aged_care']), [savedStrategies]);
    const homePlans = useMemo(() => plansOfTypes(savedStrategies as SavedPlan[], ['home_care']), [savedStrategies]);
    /* For the claim: the plans that make someone eligible first, then every other plan. */
    const claimPlans = useMemo(() => {
        const all = plansOfTypes(savedStrategies as SavedPlan[], ['aged_care', 'home_care', 'retirement_village', 'downsizer', 'granny_flat', 'cashflow_pension']);
        const qualifies = (p: SavedPlan) => (Number(p.outputs?.agePension) || 0) > 0 && currentPension < 1;
        return [...all.filter(qualifies), ...all.filter((p) => !qualifies(p))];
    }, [savedStrategies, currentPension]);

    const activeWorkflows = (Object.keys(WORKFLOW_CONFIGS) as WorkflowKey[]).filter((k) => goals?.[k]);

    /** Steps the profile already shows as done: the needs assessment, once the outcome is recorded. */
    const completedFor = (key: WorkflowKey): string[] => {
        if (key !== 'aged_care' && key !== 'home_care') return [];
        const assessed = careNeeds?.clientAcat?.status === 'yes' || (couple && careNeeds?.partnerAcat?.status === 'yes');
        return assessed ? ['acat_assessment'] : [];
    };

    const onChip = (chip: DocChip) => {
        if (chip.action.kind === 'official') void chip.action.open().catch(() => undefined);
        else if (chip.action.kind === 'sheet') openDocChipSheet(chip);
        else if (chip.action.kind === 'means') setMeansSetting(chip.action.setting);
        else if (chip.action.kind === 'pension_claim') setClaimOpen(true);
        else if (chip.action.kind === 'link') window.open(chip.action.url, '_blank', 'noopener');
        else setActivePage(chip.action.page);
    };

    /** Quick Actions: every document that applies across the active journeys, once each. */
    const quickActions = useMemo(() => {
        const seen = new Set<string>();
        const out: DocChip[] = [];
        for (const key of activeWorkflows) {
            for (const step of withPensionSteps(WORKFLOW_CONFIGS[key].steps, key, ctx)) {
                for (const chip of chipsForStep(step, ctx)) {
                    if (chip.action.kind === 'page' || chip.action.kind === 'link') continue;
                    const k = `${chip.action.kind === 'means' ? `means-${chip.action.setting}` : chip.id}-${chip.label}`;
                    if (seen.has(k)) continue;
                    seen.add(k);
                    out.push(chip);
                }
            }
        }
        return out;
    }, [activeWorkflows, ctx]);

    if (activeWorkflows.length === 0) {
        return (
            <Card>
                <SectionHeader title="Your Action Plan" />
                <div className="mt-4 p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileText size={32} className="text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Active Workflows</h3>
                    <p className="text-gray-500 text-sm mb-4">Complete your goals setup to see relevant applications and forms.</p>
                    <button onClick={() => setActivePage('Client Profile')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">Set Up Goals</button>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <SectionHeader title="Your Action Plan" />
            <div className="mt-4 space-y-4">
                {activeWorkflows.map((key) => (
                    <CollapsibleWorkflow
                        key={key}
                        workflow={WORKFLOW_CONFIGS[key]}
                        workflowKey={key}
                        completedIds={completedFor(key)}
                        isExpanded={!!expandedWorkflows[key]}
                        onToggle={() => setExpandedWorkflows((prev) => ({ ...prev, [key]: !prev[key] }))}
                        ctx={ctx}
                        onChip={onChip}
                    />
                ))}
                {quickActions.length > 0 && (
                    <div className="border-t pt-4">
                        <h4 className="font-semibold text-gray-700 mb-1">Forms and checklists</h4>
                        <p className="text-xs text-slate-500 mb-3">Pre-filled from your profile where it can be. Each opens in a new tab to print or keep beside you.</p>
                        <div className="grid grid-cols-1 gap-2">
                            {quickActions.map((chip) => (
                                <button key={`${chip.id}-${chip.label}`} type="button" onClick={() => onChip(chip)} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <FileText size={20} className="text-blue-600" />
                                        <span className="text-sm font-medium text-gray-700">{chip.action.kind === 'means' ? (chip.action.setting === 'home' ? `Support at Home contribution assessment — ${chip.label}` : `Aged care means assessment — ${chip.label}`) : chip.label}</span>
                                    </div>
                                    <ArrowRight size={16} className="text-gray-400" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {meansSetting && <MeansChooser factFind={factFind} setting={meansSetting} receivesPension={receivesPension} plans={meansSetting === 'home' ? homePlans : residentialPlans} onClose={() => setMeansSetting(null)} />}
            {claimOpen && <PensionClaimChooser factFind={factFind} plans={claimPlans} onClose={() => setClaimOpen(false)} />}
        </Card>
    );
}
